import React, { useEffect, useState } from "react";
import {
  Check,
  ChevronRight,
  ChevronDown,
  Shield,
  Users,
  CreditCard,
  Clock,
  Activity,
  Smartphone,
  Lock,
  Zap,
  LayoutDashboard,
  Calendar,
  IndianRupee,
  BadgeCheck,
  Sun,
  Moon,
} from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/MainCarousel";
import Autoplay from "embla-carousel-autoplay";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeContext";
import { useNavigate } from "react-router-dom";
import Login from "./Login";
import { useAuth } from "@/contexts/AuthContext";
import { Capacitor } from "@capacitor/core";
import { homePathFor } from "@/utils/workspace";

export default function ZenithLanding() {
  const [openFaq, setOpenFaq] = useState(null);
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [openLogin, setOpenLogin] = useState(false);

  const { user } = useAuth();

  useEffect(() => {
    const tryAutoLogin = async () => {
      try {
        if (user) {
          if (user?.role) {
            console.log("[AutoLogin] Online verified:", user.role);
            navigate(homePathFor(user, { native: Capacitor.isNativePlatform() }));
            return;
          }
        }
      } catch (e) {
        console.warn("[AutoLogin] Auto Login Failed, User not logged in");
      }
    };

    tryAutoLogin();
  }, [user]);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const autoplay = React.useRef(
    Autoplay({
      delay: 4000,
      stopOnInteraction: true,
    })
  );

  function renderStars(rating) {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex gap-1">
        {/* Full stars */}
        {Array.from({ length: fullStars }).map((_, i) => (
          <span key={`full-${i}`} className="text-amber-500">
            ★
          </span>
        ))}

        {/* Half star */}
        {hasHalfStar && (
          <span className="text-amber-500 relative inline-block">
            <span className="absolute overflow-hidden w-1/2">★</span>
            <span className="text-slate-300 dark:text-slate-600">★</span>
          </span>
        )}

        {/* Empty stars */}
        {Array.from({ length: emptyStars }).map((_, i) => (
          <span
            key={`empty-${i}`}
            className="text-slate-300 dark:text-slate-600"
          >
            ★
          </span>
        ))}
      </div>
    );
  }

  const features = {
    payroll: {
      title: "Payroll Automation",
      icon: <IndianRupee className="w-5 h-5" />,
      items: [
        "One-click auto salary calculation",
        "PF, ESI, PT, TDS auto-compute engine",
        "Customizable salary structures per grade",
        "Bank-ready export formats (ICICI, HDFC, SBI)",
        "Automated Payslip generation & emailing",
      ],
      previewLight: "/images/PayrollLight.png",
      previewDark: "/images/PayrollDark.png",
    },
    attendance: {
      title: "Attendance & Shifts",
      icon: <Clock className="w-5 h-5" />,
      items: [
        "GPS-fencing for field employees",
        "Biometric device integration",
        "Complex shift rotation management",
        "Night shift & Overtime rule engine",
        "Late-mark & Early-going policies",
      ],
      previewLight: "/images/AttendLight.png",
      previewDark: "/images/AttendDark.jpg",
    },
    leave: {
      title: "Leave Management",
      icon: <Calendar className="w-5 h-5" />,
      items: [
        "Customizable leave policies (PL, CL, SL)",
        "Auto carry-forward & encashment rules",
        "Multi-level approval workflows",
        "Holiday calendars based on location",
        "Sandwich rule configuration",
      ],
      previewLight: "/images/LeaveLight.png",
      previewDark: "/images/LeaveDark.jpg",
    },
    loans: {
      title: "Loans & Advances",
      icon: <CreditCard className="w-5 h-5" />,
      items: [
        "EMI auto-deduction from payroll",
        "Flat rate & Reducing balance calculations",
        "Loan approval workflow",
        "Pause/Resume EMI options",
        "Real-time outstanding balance tracking",
      ],
      previewLight: "/images/LoanLight.png",
      previewDark: "/images/LoanDark.png",
    },
    compliance: {
      title: "Statutory Compliance",
      icon: <Shield className="w-5 h-5" />,
      items: [
        "Ready-to-file PF Electronic Challan (ECR)",
        "ESI contribution reports",
        "Professional Tax for all Indian states",
        "Form 16 & TDS projection reports",
        "Labor Welfare Fund (LWF) automation",
      ],
      previewLight: "/images/AdviceLight.png",
      previewDark: "/images/AdviceDark.png",
    },
    employee: {
      title: "Employee Lifecycle",
      icon: <Users className="w-5 h-5" />,
      items: [
        "Digital onboarding & KYC repository",
        "Asset management tracking",
        "Document expiration alerts (Passport/CDC)",
        "Role-based access control",
        "Employee self-service portal",
      ],
      previewLight: "/images/OnboardLight.png",
      previewDark: "/images/OnboardDark.png",
    },
  };

  return (
    <div className="min-h-screen font-sans text-slate-800 selection:bg-[#3194A0]/20 selection:text-[#267680]">
      {/* Theme Toggle Button (kept as is) */}
      <div>
        <div className="absolute top-6 right-4 flex items-center gap-3 z-20 pt-10 sm:pt-0">
          {/* Theme toggle */}
          <Button
            variant="outline"
            size="icon"
            onClick={toggleTheme}
            className="h-10 w-10 rounded-lg border-gray-300 bg-white/50 hover:bg-gray-100/60 dark:border-gray-600 dark:bg-gray-800/50 dark:hover:bg-gray-700/60"
            title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDarkMode ? (
              <Sun className="h-5 w-5 text-yellow-400" />
            ) : (
              <Moon className="h-5 w-5 text-gray-700" />
            )}
          </Button>

          {/* Login */}
          <Button
            variant="outline"
            onClick={() => setOpenLogin(true)}
            className="h-10 px-5 rounded-lg border-gray-300 dark:border-gray-600"
          >
            Login
          </Button>

          {/* Sign Up */}
          {Capacitor.getPlatform() === "web" && (
            <Button
              onClick={() => navigate("/pricing")}
              className="h-10 px-5 rounded-lg bg-primary text-white hover:bg-primary/90"
            >
              Sign Up
            </Button>
          )}
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative pt-20 pb-12 overflow-hidden">
        {/* Background Glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-[#3194A0]/20 dark:bg-[#3194A0]/15 rounded-[100%] blur-3xl -z-10 opacity-60"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-10 sm:pt-0">
          <h1 className="mt-8 text-5xl md:text-6xl font-extrabold tracking-tight leading-tight text-slate-900 dark:text-white">
            India’s Most Intelligent <br />
            <span className="text-[#3194A0]">Payroll Platform</span>
          </h1>
          <p className="mt-6 max-w-2xl mx-auto text-xl leading-relaxed text-slate-600 dark:text-slate-300">
            From attendance to payroll to statutory compliance, Zenith automates
            everything—accurately, compliantly, and on time. Built for Maritime
            & Corporate teams.
          </p>
          {Capacitor.getPlatform() === "web" && (
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
              <Button
                className="h-14 px-8 text-lg bg-[#3194A0] hover:bg-[#287E88] text-white"
                onClick={() => navigate(`/pricing`)}
              >
                Start free trial
              </Button>
            </div>
          )}

          <div className="mt-12 flex justify-center items-center gap-6 text-sm flex-wrap text-slate-600 dark:text-slate-400">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500" /> ISO 27001 Security
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500" /> Auto-sync
              PF/ESI/TDS
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-500" /> 1-Click Bank Export
            </div>
          </div>

          {/* Hero Image */}
          <div className="mt-16 relative mx-auto max-w-5xl">
            {/* Glow layer */}
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-1 rounded-2xl blur-2xl opacity-20 dark:opacity-30"
            />

            {/* Mockup */}
            <div className="relative w-full border border-border">
              <img
                src="/images/lightDashboard.png"
                alt="Dashboard light"
                width={1200}
                height={700}
                className="block dark:hidden w-full h-auto"
              />

              <img
                src="/images/darkDashboard.jpg"
                alt="Dashboard dark"
                width={1200}
                height={700}
                className="hidden dark:block w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* USP / Differentiators Section */}
      <section className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white">
              Why Companies Switch to Zenith
            </h2>
            <p className="mt-4 text-slate-600 dark:text-slate-400">
              Built specifically for the complexities of the Indian workforce.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <BadgeCheck className="text-[#3194A0]" />,
                title: "100% Indian Statutory Compliant",
                desc: "Never miss a compliance deadline. Auto-updates with latest PF, ESI, PT, and TDS regulations.",
              },
              {
                icon: <Zap className="text-amber-500" />,
                title: "Lightning Fast Interface",
                desc: "Built with modern tech (React + Tailwind). No loading spinners, just instant actions.",
              },
              {
                icon: <LayoutDashboard className="text-teal-500" />,
                title: "Multi-Branch & Maritime Ready",
                desc: "Handle complex hierarchies, branch-specific holidays, and maritime crew contracts with ease.",
              },
              {
                icon: <Activity className="text-blue-500" />,
                title: "Bank Export Templates",
                desc: "Direct salary upload files for ICICI, HDFC, Kotak, SBI, and IDFC First Bank.",
              },
              {
                icon: <Lock className="text-emerald-500" />,
                title: "Enterprise Grade Security",
                desc: "CloudFront signed URLs, Role-based access, and encrypted data storage.",
              },
              {
                icon: <Smartphone className="text-purple-500" />,
                title: "Mobile-First Employee App",
                desc: "Employees can mark attendance, download payslips, and apply for leaves on the go.",
              },
            ].map((feature, idx) => (
              <div
                key={idx}
                className="p-6 rounded-2xl border border-slate-200 dark:border-slate-800 hover:shadow-lg dark:hover:shadow-slate-900/40 transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-center mb-4">
                  {React.cloneElement(feature.icon, {
                    className: `w-6 h-6 ${feature.icon.props.className}`,
                  })}
                </div>

                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                  {feature.title}
                </h3>

                <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Feature Tabs */}
      <section
        id="features"
        className="py-24 border-y border-slate-200 dark:border-slate-800"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="mt-4 text-3xl font-bold text-slate-900 dark:text-white">
              Everything you need to manage your workforce
            </h2>
          </div>

          <Carousel className="w-full" plugins={[autoplay.current]}>
            <CarouselContent>
              {Object.entries(features).map(([key, feature]) => (
                <CarouselItem key={key}>
                  <div className="rounded-2xl p-8 border border-slate-200 dark:border-slate-800 shadow-xl dark:shadow-slate-900/40 relative overflow-hidden">
                    {/* Decorative blob */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#3194A0]/10 dark:bg-[#3194A0]/5 rounded-full blur-3xl -mr-32 -mt-32 opacity-50" />
                    <div className="grid md:grid-cols-2 gap-8">
                      {/* Feature list */}
                      <ul className="space-y-4">
                        <h3 className="text-2xl font-bold mb-6 flex items-center gap-3 text-slate-900 dark:text-slate-100">
                          <div className="text-[#3194A0]">{feature.icon}</div>
                          {feature.title}
                        </h3>
                        {feature.items.map((item, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <div className="mt-1 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                              <Check className="w-3 h-3 text-primary" />
                            </div>
                            <span className="font-medium text-slate-700 dark:text-slate-300">
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                      {/* Visual / Mock */}
                      <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 flex flex-col justify-center items-center shadow-inner dark:shadow-black/40">
                        <div className="relative w-full">
                          <img
                            src={feature.previewLight}
                            alt={`${feature.title} preview light`}
                            className="block dark:hidden w-full rounded-lg object-contain"
                          />

                          <img
                            src={feature.previewDark}
                            alt={`${feature.title} preview dark`}
                            className="hidden dark:block w-full rounded-lg object-contain"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>

           
            <CarouselDots />
          </Carousel>
        </div>
      </section>

      {/* Integration Banner */}
      <section className="py-12 bg-primary/5 dark:bg-primary/20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="font-medium mb-8 text-slate-700 dark:text-slate-300">
            Seamlessly integrated with major Indian banks
          </p>

          <div className="flex flex-wrap justify-center items-center gap-10 md:gap-16">
            {[
              { src: "/images/ICICI.png", alt: "ICICI Bank" },
              { src: "/images/HDFC.png", alt: "HDFC Bank" },
              { src: "/images/Kotak.png", alt: "Kotak Mahindra Bank" },
              { src: "/images/SBI.png", alt: "State Bank of India" },
              { src: "/images/IDFC.png", alt: "IDFC First Bank" },
            ].map((bank) => (
              <img
                key={bank.alt}
                src={bank.src}
                alt={bank.alt}
                className="
            h-8 md:h-10
            max-w-[120px] md:max-w-[140px]
            object-contain
            opacity-90
            hover:opacity-100
            transition-all duration-300
          "
              />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4">
          {/* Section Heading */}
          <div className="text-center mb-16">
            <p className="text-sm font-semibold uppercase tracking-wider text-primary mb-2">
              Testimonials
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100">
              Trusted by growing Indian businesses
            </h2>
          </div>

          {/* Cards */}
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                name: "Rajesh Kumar",
                company: "Altitude Maritime Solutions",
                rating: 4.5,
                review:
                  "Zenith cut our payroll processing time by nearly 75%. Compliance alerts saved us from penalties twice already.",
              },
              {
                name: "Anita Deshmukh",
                company: "Oceanic Shipping Pvt Ltd",
                rating: 4,
                review:
                  "The statutory compliance automation is outstanding. PF, ESI, PT — everything just works without manual checks.",
              },
              {
                name: "Vikram Iyer",
                company: "Harborline Logistics",
                rating: 5,
                review:
                  "Zenith gives us confidence during audits. Reports are clean, accurate, and always ready when inspectors ask.",
              },
              {
                name: "Suresh Patel",
                company: "BlueWave Crewing Services",
                rating: 5,
                review:
                  "Payroll is now streamlined, predictable, and fully compliant with Indian labor regulations—month after month.",
              },
            ].map((t, index) => (
              <div
                key={index}
                className="rounded-2xl border border-slate-200 dark:border-slate-800 hover:bg-primary/5 p-6 shadow-sm hover:shadow-lg transition-shadow"
              >
                {/* Stars */}
                <div className="mb-4 text-lg leading-none">
                  {renderStars(t.rating)}
                </div>

                {/* Review */}
                <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300 mb-6">
                  “{t.review}”
                </p>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                    <span className="text-sm font-bold text-slate-600 dark:text-slate-300">
                      {t.name.charAt(0)}
                    </span>
                  </div>

                  <div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {t.name}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {t.company}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section>
        <div className="max-w-7xl mx-auto px-4 mb-20">
          <h2 className="text-3xl font-bold text-center mb-12 text-slate-900 dark:text-white">
            Frequently Asked Questions
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                q: "Is Zenith fully compliant with Indian labor laws?",
                a: "Yes, absolutely. We update our system within 24 hours of any government notification regarding PF, ESI, PT, TDS, or LWF laws across all states.",
              },
              {
                q: "Can I import my data from Excel or another software?",
                a: "Yes, we have bulk import templates for employees, past salaries, and leave balances. Our onboarding team helps you migrate data smoothly.",
              },
              {
                q: "Is my data secure?",
                a: "We use ISO 27001 certified data centers. Your data is encrypted at rest and in transit. We use role-based access control so only authorized personnel can view sensitive salary data.",
              },
              {
                q: "Does it support employees working in shifts?",
                a: "Yes, Zenith has a powerful shift management engine supporting rotational shifts, night shifts, and flexible timings, linked directly to attendance and payroll.",
              },
            ].map((faq, idx) => (
              <div
                key={idx}
                className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(idx)}
                  className="w-full flex justify-between items-center p-5 text-left
              text-slate-900 dark:text-white
              hover:bg-slate-50 dark:hover:bg-slate-900/40
              transition-colors"
                >
                  <span className="font-medium">{faq.q}</span>

                  {openFaq === idx ? (
                    <ChevronDown className="text-[#3194A0]" />
                  ) : (
                    <ChevronRight className="text-slate-400 dark:text-slate-500" />
                  )}
                </button>

                {openFaq === idx && (
                  <div className="p-5 border-t border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 leading-relaxed">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-20">
        <footer className="w-full bg-primary/20 text-primary py-4">
          <div
            className="
        max-w-7xl mx-auto px-4
        flex flex-col gap-4
        items-center text-center
       
        md:flex-row md:items-center md:justify-between md:text-left
      "
          >
            {/* Copyright */}
            <div className="text-sm opacity-90">
              © {new Date().getFullYear()} Element Tree. All rights reserved
            </div>

            {/* Powered by */}
            <div
              className="flex items-center gap-2 text-sm cursor-pointer"
              onClick={() => window.open("https://elementree.co.in/", "_blank")}
            >
              <span>Powered by</span>

              <img
                src="/images/logo.png"
                alt="Element Tree"
                className="h-6 w-auto"
              />
            </div>

            {/* Privacy / Terms */}
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                <a
                  onClick={() => navigate("/policy", { state: "term" })}
                  className="text-primary font-medium hover:underline cursor-pointer"
                >
                  Terms of Service
                </a>

                <span className="mx-2">|</span>

                <a
                  onClick={() => navigate("/policy", { state: "policy" })}
                  className="text-primary font-medium hover:underline cursor-pointer"
                >
                  Privacy Policy
                </a>
                <span className="mx-2">|</span>

                <a
                  onClick={() => navigate("/contact")}
                  className="text-primary font-medium hover:underline cursor-pointer"
                >
                  Contact Us
                </a>
              </p>
            </div>
          </div>
        </footer>
      </section>

      <Login open={openLogin} onOpenChange={setOpenLogin} />
    </div>
  );
}
